import { useEffect, useState } from "react";

import { css } from "@emotion/react";

import { colors } from "@dotnet/telematics-design-system";

import ButtonDefault from "../ButtonDefault";
import { TOOLTIP_PLACEMENT, TooltipGeneral } from "../Tooltips";
import Container from "./Container";
import { getFilledPercentage } from "./functions";
import Loader from "./Loader";
import { setBarItemStyle } from "./sharedStyles";
import { ProgressBarSingleProps } from "./types";

const ProgressBarSingle = ({
    id,
    titleLeft,
    titleRight,
    totalCount,
    filledCount,
    filledColor,
    totalColor,
    titleColor,
    titleRightColor,
    hoverColor,
    tooltipContent,
    isLoading,
    customValueStyles,
    onHoverOn,
    onHoverOff,
    onClick
}: ProgressBarSingleProps) => {
    const { gray300 } = colors;

    const [filledWidth, setFilledWidth] = useState("0%");

    useEffect(() => {
        if (isLoading) {
            setFilledWidth("0%");
            return;
        }

        // Animate the width change after the data is set
        const timer = setTimeout(() => {
            setFilledWidth(getFilledPercentage(totalCount, filledCount));
        }, 10);

        return () => clearTimeout(timer);
    }, [totalCount, filledCount, isLoading]);

    const getBgColor = () => {
        if (filledCount === 0) {
            return gray300;
        }

        return totalColor || "unset";
    };

    const getBorderRadius = () => {
        if (!totalColor || filledCount >= totalCount) {
            return "100px";
        }

        return "100px 0 0 100px";
    };

    const handleHoverOn = () => onHoverOn?.(id);
    const handleHoverOff = () => onHoverOff?.(id);

    return (
        <Container
            id={id}
            titleLeft={titleLeft}
            titleRight={titleRight}
            titleColor={titleColor}
            titleRightColor={titleRightColor}
            tooltipContent={tooltipContent}
            customValueStyles={customValueStyles}
            onHoverOn={onHoverOn}
            onHoverOff={onHoverOff}
            onValueClick={onClick}
        >
            {isLoading ? (
                <Loader />
            ) : (
                <div
                    css={css({
                        display: "flex",
                        width: "100%",
                        background: getBgColor(),
                        borderRadius: "100px",
                        margin: "4px 0"
                    })}
                >
                    <TooltipGeneral
                        title={tooltipContent}
                        placement={TOOLTIP_PLACEMENT.Top}
                    >
                        <ButtonDefault
                            onMouseEnter={handleHoverOn}
                            onMouseLeave={handleHoverOff}
                            onTouchStart={handleHoverOn}
                            onTouchEnd={handleHoverOff}
                            onClick={onClick}
                            css={css({
                                width: filledWidth,
                                cursor: tooltipContent ? "pointer" : "auto",
                                transition: "width 0.3s ease"
                            })}
                        >
                            <span
                                css={setBarItemStyle(
                                    filledColor,
                                    getBorderRadius(),
                                    hoverColor
                                )}
                            />
                        </ButtonDefault>
                    </TooltipGeneral>
                </div>
            )}
        </Container>
    );
};

export { ProgressBarSingle };
